import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { API_BASE_URL } from '@/services/api';

/**
 * Blocks rendering until the backend has finished starting (`GET /ready` -> 200).
 *
 * On a cold start the backend can take tens of seconds (Tortoise init, schema
 * generation on large DBs). Without this, every page fires its queries into a
 * not-yet-serving API and sits on an infinite spinner. We poll /ready and show a
 * "starting" screen with a retry, so the app loads cleanly once the API is up.
 */
export function ServerReadyGate({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [attempt, setAttempt] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const check = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/ready`, { cache: 'no-store' });
        if (res.ok) {
          if (!cancelledRef.current) setReady(true);
          return;
        }
      } catch {
        // Not reachable yet — keep polling.
      }
      if (!cancelledRef.current) {
        timer = setTimeout(check, 2000);
      }
    };

    check();
    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [attempt]);

  useEffect(() => {
    if (ready) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [ready]);

  if (ready) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background px-6 text-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <div className="text-sm font-medium">Starting Conbersa…</div>
      <p className="max-w-sm text-xs text-muted-foreground">
        Waiting for the server to finish starting. First boot after a restart can take up to a
        minute.
        {elapsed > 0 && <span className="block mt-1 font-mono">{elapsed}s</span>}
      </p>
      {elapsed >= 20 && (
        <button
          type="button"
          className="text-xs text-primary underline"
          onClick={() => {
            setElapsed(0);
            setAttempt((a) => a + 1);
          }}
        >
          Still not loading? Retry
        </button>
      )}
    </div>
  );
}
